'use server';

import { z } from 'zod';
import { and, eq, sql } from 'drizzle-orm';
import { randomBytes } from 'crypto';
import { db } from '@/lib/db/drizzle';
import {
  User,
  users,
  teams,
  teamMembers,
  activityLogs,
  type NewUser,
  type NewTeam,
  type NewTeamMember,
  type NewActivityLog,
  ActivityType,
  invitations
} from '@/lib/db/schema';
import { comparePasswords, hashPassword, setSession } from '@/lib/auth/session';
import { redirect } from 'next/navigation';
import { cookies, headers } from 'next/headers';
import { createCheckoutSession } from '@/lib/payments/stripe';
import { getUser, getUserWithTeam } from '@/lib/db/queries';
import {
  validatedAction,
  validatedActionWithUser
} from '@/lib/auth/middleware';

import { sendInvitationEmail, sendWelcomeCredentialsEmail } from '@/lib/email';
import { ROLE_PRESETS, TeamRole } from '@/lib/permissions';
import { getFreePlan } from '@/lib/db/queries';
import { enforceLimit } from '@/lib/limits';
import { rateLimit, RATE_LIMITS } from '@/lib/rate-limit';

async function logActivity(
  teamId: number | null | undefined,
  userId: number,
  type: ActivityType,
  ipAddress?: string
) {
  if (teamId === null || teamId === undefined) {
    return;
  }
  const newActivity: NewActivityLog = {
    teamId,
    userId,
    action: type,
    ipAddress: ipAddress || ''
  };
  await db.insert(activityLogs).values(newActivity);
}

const signInSchema = z.object({
  email: z.string().email().min(3).max(255),
  password: z.string().min(8).max(100)
});

export const signIn = validatedAction(signInSchema, async (data, formData) => {
  const { email, password } = data;

  const hdrs = await headers();
  const ip = hdrs.get('x-forwarded-for')?.split(',')[0].trim() || hdrs.get('x-real-ip') || '127.0.0.1';
  const rl = rateLimit(`auth:sign-in:${ip}`, RATE_LIMITS.auth);
  if (!rl.success) {
    return { error: 'Too many attempts. Please try again later.', email, password };
  }

  const userWithTeam = await db
    .select({
      user: users,
      team: teams
    })
    .from(users)
    .leftJoin(teamMembers, eq(users.id, teamMembers.userId))
    .leftJoin(teams, eq(teamMembers.teamId, teams.id))
    .where(eq(users.email, email))
    .limit(1);

  if (userWithTeam.length === 0) {
    return {
      error: 'Invalid email or password. Please try again.',
      email,
      password
    };
  }

  const { user: foundUser, team: foundTeam } = userWithTeam[0];

  const isPasswordValid = await comparePasswords(
    password,
    foundUser.passwordHash
  );

  if (!isPasswordValid) {
    return {
      error: 'Invalid email or password. Please try again.',
      email,
      password
    };
  }

  await Promise.all([
    setSession(foundUser),
    logActivity(foundTeam?.id, foundUser.id, ActivityType.SIGN_IN)
  ]);

  const redirectTo = formData.get('redirect') as string | null;
  if (redirectTo === 'checkout') {
    const priceId = formData.get('priceId') as string;
    return createCheckoutSession({ team: foundTeam, priceId });
  }

  redirect('/dashboard');
});

const signUpSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  inviteId: z.string().optional()
});

export const signUp = validatedAction(signUpSchema, async (data, formData) => {
  const { email, password, inviteId } = data;

  const hdrs = await headers();
  const ip = hdrs.get('x-forwarded-for')?.split(',')[0].trim() || hdrs.get('x-real-ip') || '127.0.0.1';
  const rl = rateLimit(`auth:sign-up:${ip}`, RATE_LIMITS.auth);
  if (!rl.success) {
    return { error: 'Too many attempts. Please try again later.', email, password };
  }

  const existingUser = await db
    .select()
    .from(users)
    .where(eq(users.email, email))
    .limit(1);

  if (existingUser.length > 0) {
    return {
      error: 'Failed to create user. Please try again.',
      email,
      password
    };
  }

  const passwordHash = await hashPassword(password);

  const newUser: NewUser = {
    email,
    passwordHash,
    role: 'owner' 
  };

  const [createdUser] = await db.insert(users).values(newUser).returning();

  if (!createdUser) {
    return {
      error: 'Failed to create user. Please try again.',
      email,
      password
    };
  }

  let teamId: number;
  let userRole: string;
  let createdTeam: typeof teams.$inferSelect | null = null;

  if (inviteId) {
    
    const [invitation] = await db
      .select()
      .from(invitations)
      .where(
        and(
          eq(invitations.id, parseInt(inviteId)),
          eq(invitations.email, email),
          eq(invitations.status, 'pending')
        )
      )
      .limit(1);

    if (invitation) {
      teamId = invitation.teamId;
      userRole = invitation.role;

      await db
        .update(invitations)
        .set({ status: 'accepted' })
        .where(eq(invitations.id, invitation.id));

      await logActivity(teamId, createdUser.id, ActivityType.ACCEPT_INVITATION);

      [createdTeam] = await db
        .select()
        .from(teams)
        .where(eq(teams.id, teamId))
        .limit(1);
    } else {
      return { error: 'Invalid or expired invitation.', email, password };
    }
  } else {
    const newTeam: NewTeam = {
      name: (formData.get('companyName') as string) || `${email}'s Team`,
      companyPhone: (formData.get('companyPhone') as string) || (formData.get('phone') as string) || undefined,
      companyAddress: (formData.get('companyAddress') as string) || undefined,
      companyWebsite: (formData.get('companyWebsite') as string) || undefined,
      currency: ((formData.get('currency') as string) || 'USD').toUpperCase().trim(),
    };

    [createdTeam] = await db.insert(teams).values(newTeam).returning();

    const freePlan = await getFreePlan();
    if (freePlan) {
      await db.update(teams)
        .set({ 
          planId: freePlan.id,
          subscriptionStatus: 'active' 
        })
        .where(eq(teams.id, createdTeam.id));
    }

    if (!createdTeam) {
      return {
        error: 'Failed to create team. Please try again.',
        email,
        password
      };
    }

    teamId = createdTeam.id;
    userRole = 'owner';

    await logActivity(teamId, createdUser.id, ActivityType.CREATE_TEAM);
  }

  const newTeamMember: NewTeamMember = {
    userId: createdUser.id,
    teamId: teamId,
    role: userRole
  };

  await Promise.all([
    db.insert(teamMembers).values(newTeamMember),
    logActivity(teamId, createdUser.id, ActivityType.SIGN_UP),
    setSession(createdUser)
  ]);

  const redirectTo = formData.get('redirect') as string | null;
  if (redirectTo === 'checkout') {
    const priceId = formData.get('priceId') as string;
    return createCheckoutSession({ team: createdTeam, priceId });
  }

  redirect('/dashboard');
});

export async function signOut() {
  const user = (await getUser()) as User;
  const userWithTeam = await getUserWithTeam(user.id);
  await logActivity(userWithTeam?.teamId, user.id, ActivityType.SIGN_OUT);
  (await cookies()).delete('session');
}

const updatePasswordSchema = z.object({
  currentPassword: z.string().min(8).max(100),
  newPassword: z.string().min(8).max(100),
  confirmPassword: z.string().min(8).max(100)
});

export const updatePassword = validatedActionWithUser(
  updatePasswordSchema,
  async (data, _, user) => {
    const { currentPassword, newPassword, confirmPassword } = data;

    const isPasswordValid = await comparePasswords(
      currentPassword,
      user.passwordHash
    );

    if (!isPasswordValid) {
      return {
        currentPassword,
        newPassword,
        confirmPassword,
        error: 'Current password is incorrect.'
      };
    }

    if (currentPassword === newPassword) {
      return {
        currentPassword,
        newPassword,
        confirmPassword,
        error: 'New password must be different from the current password.'
      };
    }

    if (confirmPassword !== newPassword) {
      return {
        currentPassword,
        newPassword,
        confirmPassword,
        error: 'New password and confirmation password do not match.'
      };
    }

    const newPasswordHash = await hashPassword(newPassword);
    const userWithTeam = await getUserWithTeam(user.id);

    await Promise.all([
      db
        .update(users)
        .set({ passwordHash: newPasswordHash })
        .where(eq(users.id, user.id)),
      logActivity(userWithTeam?.teamId, user.id, ActivityType.UPDATE_PASSWORD)
    ]);

    return {
      success: 'Password updated successfully.'
    };
  }
);

const deleteAccountSchema = z.object({
  password: z.string().min(8).max(100)
});

export const deleteAccount = validatedActionWithUser(
  deleteAccountSchema,
  async (data, _, user) => {
    const { password } = data;

    const isPasswordValid = await comparePasswords(password, user.passwordHash);
    if (!isPasswordValid) {
      return {
        password,
        error: 'Incorrect password. Account deletion failed.'
      };
    }

    const userWithTeam = await getUserWithTeam(user.id);

    await logActivity(
      userWithTeam?.teamId,
      user.id,
      ActivityType.DELETE_ACCOUNT
    );

    
    await db
      .update(users)
      .set({
        deletedAt: sql`CURRENT_TIMESTAMP`,
        email: sql`CONCAT(email, '-', id, '-deleted')` 
      })
      .where(eq(users.id, user.id));

    if (userWithTeam?.teamId) {
      await db
        .delete(teamMembers)
        .where(
          and(
            eq(teamMembers.userId, user.id),
            eq(teamMembers.teamId, userWithTeam.teamId)
          )
        );
    }

    (await cookies()).delete('session');
    redirect('/sign-in');
  }
);

const updateAccountSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  email: z.string().email('Invalid email address'),
  enableSignature: z.string().optional(),
});

export const updateAccount = validatedActionWithUser(
  updateAccountSchema,
  async (data, _, user) => {
    const { name, email, enableSignature } = data;
    const userWithTeam = await getUserWithTeam(user.id);

    await Promise.all([
      db.update(users).set({
        name,
        email,
        enableSignature: enableSignature === 'on',
      }).where(eq(users.id, user.id)),
      logActivity(userWithTeam?.teamId, user.id, ActivityType.UPDATE_ACCOUNT)
    ]);

    return { name, success: 'Account updated successfully.' };
  }
);

const removeTeamMemberSchema = z.object({
  memberId: z.coerce.number()
});

export const removeTeamMember = validatedActionWithUser(
  removeTeamMemberSchema,
  async (data, _, user) => {
    const { memberId } = data;
    const userWithTeam = await getUserWithTeam(user.id);

    if (!userWithTeam?.teamId) {
      return { error: 'User is not part of a team' };
    }

    await db
      .delete(teamMembers)
      .where(
        and(
          eq(teamMembers.id, memberId),
          eq(teamMembers.teamId, userWithTeam.teamId)
        )
      );

    await logActivity(
      userWithTeam.teamId,
      user.id,
      ActivityType.REMOVE_TEAM_MEMBER
    );

    return { success: 'Team member removed successfully' };
  }
);

/** Generate a random 8-digit numeric password */
function generatePassword(): string {
  return Math.floor(10000000 + Math.random() * 90000000).toString();
}

const inviteTeamMemberSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  role: z.enum(['owner', 'admin', 'agent'])
});

export const inviteTeamMember = validatedActionWithUser(
  inviteTeamMemberSchema,
  async (data, _, user) => {
    const { name, email, phone, role } = data;
    const cleanPhone = phone?.trim() || null;
    const userWithTeam = await getUserWithTeam(user.id);

    if (!userWithTeam?.teamId) {
      return { error: 'User is not part of a team' };
    }

    try {
      await enforceLimit(userWithTeam.teamId, 'users');
    } catch (e: any) {
      return { error: e.message };
    }

    const team = await db.query.teams.findFirst({
      where: eq(teams.id, userWithTeam.teamId),
      columns: { name: true }
    });

    if (!team) {
      return { error: 'Team not found' };
    }

    const existingMember = await db
      .select()
      .from(users)
      .leftJoin(teamMembers, eq(users.id, teamMembers.userId))
      .where(
        and(eq(users.email, email), eq(teamMembers.teamId, userWithTeam.teamId))
      )
      .limit(1);

    if (existingMember.length > 0) {
      return { error: 'User is already a member of this team' };
    }

    // Generate credentials
    const plainPassword = generatePassword();
    const passwordHash = await hashPassword(plainPassword);

    // Check if user already exists in users table
    const existingUser = await db.query.users.findFirst({
      where: eq(users.email, email),
      columns: { id: true, name: true, phone: true, deletedAt: true }
    });

    let memberUserId: number;
    if (existingUser) {
      memberUserId = existingUser.id;
      await db
        .update(users)
        .set({
          passwordHash,
          displayPassword: plainPassword,
          name: existingUser.name || name,
          phone: cleanPhone || existingUser.phone || null,
          deletedAt: null,
        })
        .where(eq(users.id, memberUserId));
    } else {
      const [newUser] = await db
        .insert(users)
        .values({
          email,
          name,
          phone: cleanPhone,
          passwordHash,
          displayPassword: plainPassword,
          role: 'member',
        })
        .returning({ id: users.id });
      memberUserId = newUser.id;
    }

    const memberRole = role as TeamRole;
    const defaultPermissions = ROLE_PRESETS[memberRole] || ROLE_PRESETS.agent;

    await db.insert(teamMembers).values({
      userId: memberUserId,
      teamId: userWithTeam.teamId,
      role: memberRole,
      permissions: defaultPermissions,
    });

    // Remove any pending invitations for this email in this team
    await db
      .delete(invitations)
      .where(
        and(
          eq(invitations.email, email),
          eq(invitations.teamId, userWithTeam.teamId)
        )
      );

    await logActivity(
      userWithTeam.teamId,
      user.id,
      ActivityType.INVITE_TEAM_MEMBER
    );

    let emailSent = false;
    try {
      await sendWelcomeCredentialsEmail(email, plainPassword, team.name, name);
      emailSent = true;
    } catch (emailErr) {
      console.error('Failed to send welcome credentials email:', emailErr);
    }

    return {
      success: `Team member ${name} added successfully!`,
      credentials: {
        name,
        email,
        phone: cleanPhone || undefined,
        password: plainPassword,
        role: memberRole,
        emailSent,
      },
    };
  }
);

const revokeInvitationSchema = z.object({
  inviteId: z.coerce.number()
});

export const revokeInvitation = validatedActionWithUser(
  revokeInvitationSchema,
  async (data, _, user) => {
    const { inviteId } = data;
    const userWithTeam = await getUserWithTeam(user.id);

    if (!userWithTeam?.teamId) {
      return { error: 'User is not part of a team' };
    }

    await db
      .delete(invitations)
      .where(
        and(
          eq(invitations.id, inviteId),
          eq(invitations.teamId, userWithTeam.teamId)
        )
      );

    await logActivity(
      userWithTeam.teamId,
      user.id,
      ActivityType.REMOVE_TEAM_MEMBER
    );

    return { success: 'Invitation revoked successfully' };
  }
);

const resendInvitationSchema = z.object({
  inviteId: z.coerce.number()
});

export const resendInvitation = validatedActionWithUser(
  resendInvitationSchema,
  async (data, _, user) => {
    const { inviteId } = data;
    const userWithTeam = await getUserWithTeam(user.id);

    if (!userWithTeam?.teamId) {
      return { error: 'User is not part of a team' };
    }

    const invitation = await db.query.invitations.findFirst({
      where: and(
        eq(invitations.id, inviteId),
        eq(invitations.teamId, userWithTeam.teamId)
      )
    });

    if (!invitation) {
      return { error: 'Invitation not found' };
    }

    const team = await db.query.teams.findFirst({
      where: eq(teams.id, userWithTeam.teamId),
      columns: { name: true }
    });

    if (!team) {
      return { error: 'Team not found' };
    }

    // Auto-create/activate member and send credentials directly
    const plainPassword = generatePassword();
    const passwordHash = await hashPassword(plainPassword);

    const existingUser = await db.query.users.findFirst({
      where: eq(users.email, invitation.email),
      columns: { id: true, name: true }
    });

    let memberUserId: number;
    if (existingUser) {
      memberUserId = existingUser.id;
      await db
        .update(users)
        .set({
          passwordHash,
          name: existingUser.name || invitation.inviteeName || undefined,
          deletedAt: null,
        })
        .where(eq(users.id, memberUserId));
    } else {
      const [newUser] = await db
        .insert(users)
        .values({
          email: invitation.email,
          name: invitation.inviteeName || undefined,
          passwordHash,
          role: 'member',
        })
        .returning({ id: users.id });
      memberUserId = newUser.id;
    }

    const memberRole = (invitation.role as TeamRole) || 'agent';
    const defaultPermissions = ROLE_PRESETS[memberRole] || ROLE_PRESETS.agent;

    const existingMember = await db.query.teamMembers.findFirst({
      where: and(
        eq(teamMembers.userId, memberUserId),
        eq(teamMembers.teamId, userWithTeam.teamId)
      )
    });

    if (!existingMember) {
      await db.insert(teamMembers).values({
        userId: memberUserId,
        teamId: userWithTeam.teamId,
        role: memberRole,
        permissions: defaultPermissions,
      });
    }

    // Delete the pending invitation since the member is now active
    await db.delete(invitations).where(eq(invitations.id, inviteId));

    let emailSent = false;
    try {
      await sendWelcomeCredentialsEmail(
        invitation.email,
        plainPassword,
        team.name,
        invitation.inviteeName ?? undefined
      );
      emailSent = true;
    } catch (emailErr) {
      console.error('Failed to send credentials email on resend:', emailErr);
    }

    return {
      success: 'Account created and credentials sent to member!',
      credentials: {
        name: invitation.inviteeName || invitation.email,
        email: invitation.email,
        password: plainPassword,
        role: memberRole,
        emailSent,
      },
    };
  }
);